import { useEffect, useState } from "react";
import { Button, Spinner, Text, TextField } from "@merge-api/merge-javascript-shared";
import { Search } from "lucide-react";
import { showErrorToast, showSuccessToast } from "../../shared/Toasts";
import { conductorDeleteJson, conductorPut } from "../conductorApi";
import { useConductorResource } from "../hooks/useConductorResource";
import AccountSpeedCard from "./components/AccountSpeedCard";
import OrganizationOverrideCard from "./components/OrganizationOverrideCard";
import { AccountSyncStatus } from "./types";

// Sync speed overrides. Conductor resolves `speed` itself from the
// organization's billing entitlement and will overwrite direct edits, so the
// admin writes an override layer on top instead. Reads are per linked account:
// conductor has no RPC to list an organization's accounts.
const AccountSpeedsPage = () => {
  // state
  const [accountInput, setAccountInput] = useState("");
  const [linkedAccountId, setLinkedAccountId] = useState("");
  const [reloadToken, setReloadToken] = useState(0);
  const [draftSpeed, setDraftSpeed] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // hooks
  const { data: status, isLoading } = useConductorResource<AccountSyncStatus>(
    linkedAccountId ? `/conductor/account-sync-statuses/${linkedAccountId}` : null,
    "Error fetching linked account",
    reloadToken
  );

  // Seed the picker from the existing override only. With none set it stays
  // blank rather than preselecting the effective speed, which would read as an
  // override that isn't there.
  useEffect(() => {
    if (status) setDraftSpeed(status.speed_override ?? "");
  }, [status]);

  // event handlers
  const setOverride = async () => {
    setIsSaving(true);
    const { ok, error } = await conductorPut(
      `/conductor/account-sync-statuses/${linkedAccountId}/speed-override`,
      {
        speed: draftSpeed,
      }
    );
    setIsSaving(false);
    if (ok) {
      showSuccessToast("Override set");
      setReloadToken((token) => token + 1);
    } else {
      showErrorToast(error || "Failed to set override");
    }
  };

  const clearOverride = async () => {
    setIsSaving(true);
    // The response carries the speed the account fell back to, so it is read
    // rather than assumed.
    const { ok, data, error } = await conductorDeleteJson<AccountSyncStatus>(
      `/conductor/account-sync-statuses/${linkedAccountId}/speed-override`
    );
    setIsSaving(false);
    if (ok) {
      showSuccessToast(
        data
          ? `Reverted to policy: ${data.effective_speed.replace(/^ACCOUNT_SYNC_SPEED_/, "")}`
          : "Override cleared"
      );
      setReloadToken((token) => token + 1);
    } else {
      showErrorToast(error || "Failed to clear override");
    }
  };

  // render
  return (
    <>
      <div className="mb-6">
        <Text variant="h5">Account speeds</Text>
        <Text variant="sm" className="mt-1 block text-gray-60">
          Conductor resolves each account's speed from its organization's billing entitlement. An
          override set here takes precedence and survives that re-resolution.
        </Text>
      </div>

      {/* Above the columns so both cards start on the same line. */}
      <div className="mb-6 flex flex-wrap items-end gap-2">
        <div className="w-96">
          <Text variant="h6" className="mb-2">
            Linked account ID
          </Text>
          <TextField
            value={accountInput}
            onChange={(event) => setAccountInput(event.target.value)}
            placeholder="paste a linked account UUID"
          />
        </div>
        <Button
          size="md"
          leftIcon={<Search size={16} />}
          disabled={accountInput.trim() === ""}
          onClick={() => setLinkedAccountId(accountInput.trim())}
        >
          Look up
        </Button>
      </div>

      {/* Account speed is the page; the org override is a narrower rail beside it. */}
      <div className="flex items-stretch gap-8">
        <div className="min-w-0 flex-1">
          {isLoading && linkedAccountId ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : linkedAccountId && !status ? (
            <Text variant="md" className="block py-6 text-gray-60">
              No conductor row for this linked account. Rows are created by sync activity and
              backfill, not from here.
            </Text>
          ) : status ? (
            <AccountSpeedCard
              status={status}
              draftSpeed={draftSpeed}
              onDraftSpeedChange={setDraftSpeed}
              isSaving={isSaving}
              onSetOverride={setOverride}
              onClearOverride={clearOverride}
            />
          ) : null}
        </div>

        <div className="w-[420px] shrink-0">
          <OrganizationOverrideCard
            prefillOrganizationId={status?.organization_id}
            onOverrideChanged={() => setReloadToken((token) => token + 1)}
          />
        </div>
      </div>
    </>
  );
};

export default AccountSpeedsPage;
